/**
 * Auth Service - Hybrid Authentication
 * 
 * 🔒 SECURITY:
 * - PRIMARY: Google Sign-In (OAuth2) — password never touches our app
 * - FALLBACK: Email/Password via Firebase SDK (for non-Google accounts like @awarenesstool.it)
 *   The email/password flow uses Firebase SDK over HTTPS/TLS.
 *   Note: the password is in the request body within the encrypted TLS tunnel,
 *   which is standard for all email/password auth on the web.
 * 
 * Firebase automatically links accounts with matching emails,
 * so existing email/password users can migrate to Google Sign-In
 * if their email is a Google/Workspace account.
 */

import { initializeApp, getApps } from 'firebase/app';
import {
    getAuth,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../config/firebase';

// Google OAuth2 Provider configuration
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Force account selection every time (prevents auto-login with wrong account)
googleProvider.setCustomParameters({
    prompt: 'select_account'
});

export const AuthService = {
    /**
     * 🔒 Login with Google Sign-In (OAuth2 Popup) — RECOMMENDED
     * The password is NEVER handled by our application.
     * User authenticates directly with Google on accounts.google.com.
     * 
     * @returns {Promise<Object>} Firebase user object
     */
    loginWithGoogle: async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            return result.user;
        } catch (error) {
            if (error.code === 'auth/popup-closed-by-user') {
                throw new Error('LOGIN_CANCELLED');
            }
            if (error.code === 'auth/popup-blocked') {
                // Fallback to redirect if popup is blocked
                console.warn('Popup blocked, falling back to redirect...');
                await signInWithRedirect(auth, googleProvider);
                return null;
            }
            console.error("Google Sign-In Error:", error);
            throw error;
        }
    },

    /**
     * Login with Email/Password — LEGACY FALLBACK
     * For accounts that can't use Google Sign-In (e.g., @awarenesstool.it without Google Workspace)
     * 
     * ⚠️ The password transits in the HTTPS request body to Firebase servers.
     * This is standard email/password auth, protected by TLS encryption in transit.
     * 
     * @param {string} email
     * @param {string} password
     * @returns {Promise<Object>} Firebase user object
     */
    loginWithEmail: async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return userCredential.user;
        } catch (error) {
            console.error("Email Auth Error:", error);
            throw error;
        }
    },

    /**
     * Handle redirect result (called on page load after redirect sign-in)
     * @returns {Promise<Object|null>} Firebase user or null
     */
    handleRedirectResult: async () => {
        try {
            const result = await getRedirectResult(auth);
            return result ? result.user : null;
        } catch (error) {
            console.error("Redirect Result Error:", error);
            throw error;
        }
    },

    /**
     * Create user in Firebase Auth with initial/test password without logging out the current admin
     * @param {string} email
     * @param {string} password
     * @returns {Promise<{ uid: string|null, created: boolean, reason?: string }>}
     */
    createUserWithPassword: async (email, password) => {
        try {
            const firebaseConfig = {
                apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
                authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
                projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
                storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
                messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
                appId: import.meta.env.VITE_FIREBASE_APP_ID,
                measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
            };

            const secondaryAppName = 'SecondaryAuthApp';
            const existingApps = getApps();
            let secondaryApp = existingApps.find(a => a.name === secondaryAppName);
            if (!secondaryApp) {
                secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
            }
            const secondaryAuth = getAuth(secondaryApp);
            const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email.toLowerCase().trim(), password);
            const uid = userCredential.user.uid;
            await signOut(secondaryAuth);
            return { uid, created: true };
        } catch (error) {
            console.warn("createUserWithPassword error:", error);
            if (error.code === 'auth/email-already-in-use') {
                return { uid: null, created: false, reason: 'EMAIL_EXISTS' };
            }
            throw error;
        }
    },

    /**
     * Send password reset email
     * @param {string} email
     */
    resetPassword: async (email) => {
        try {
            const cleanEmail = email.trim().toLowerCase();
            const actionCodeSettings = {
                url: window.location.origin,
                handleCodeInApp: false
            };
            try {
                await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
            } catch (errWithSettings) {
                // If actionCodeSettings domain is not whitelisted, fallback to basic call
                console.warn("Retrying sendPasswordResetEmail without actionCodeSettings:", errWithSettings.message);
                await sendPasswordResetEmail(auth, cleanEmail);
            }
            return true;
        } catch (error) {
            console.error("Reset Password Error:", error);
            throw error;
        }
    },

    /**
     * Ensure user exists in Firebase Auth (creating them with a temp password if missing)
     * and dispatch the password reset email.
     * @param {string} email
     * @param {string} tempPassword
     * @returns {Promise<{ createdInAuth: boolean, uid: string|null, tempPassword: string }>}
     */
    ensureUserInAuthAndReset: async (email, tempPassword = 'Awareness2026!') => {
        const cleanEmail = email.trim().toLowerCase();
        let wasCreated = false;
        let userUid = null;

        // 1. Attempt to create in Firebase Auth if not already existing
        try {
            const authRes = await AuthService.createUserWithPassword(cleanEmail, tempPassword);
            if (authRes.created) {
                wasCreated = true;
                userUid = authRes.uid;
            }
        } catch (err) {
            console.warn("User already in Auth or provision notice:", err.message);
        }

        // 2. Dispatch reset email (guaranteed to find an account if just created)
        await AuthService.resetPassword(cleanEmail);

        return {
            createdInAuth: wasCreated,
            uid: userUid,
            tempPassword
        };
    },

    /**
     * Logout
     */
    logout: async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Logout Error:", error);
            throw error;
        }
    },

    /**
     * Subscribe to auth state changes
     * @param {Function} callback 
     * @returns {Function} unsubscribe function
     */
    observeAuth: (callback) => {
        return onAuthStateChanged(auth, callback);
    }
};
